import React, { useEffect, useRef, useMemo, useCallback } from "react";
import * as echarts from "echarts/core";
import { LineChart as ELineChart } from "echarts/charts";
import {
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { formatNumberWithIntl } from "@wavvest/shared-utils";

type ProjectedSaving = {
  year: number;
  percentile_5: number;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_95: number;
  [key: string]: number;
};

echarts.use([
  ELineChart,
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
  CanvasRenderer,
]);

export type lineChartIndicatorProps = {
  x: number;
  y: number;
  name: string;
  value: number;
};

interface ConfidenceChartProps {
  data: ProjectedSaving[];
  width?: number;
  height?: number;
  themeColors?: {
    backgroundColor: string;
    textColor: string;
    lineColor: string;
    areaColors: {
      confidence95: string;
      confidence75: string;
    };
  };
  indicator?: {
    x: number;
    y: number;
    value: number;
    name: string;
  } | null;
  setIndicator: (
    indicator: {
      x: number;
      y: number;
      value: number;
      name: string;
    } | null,
  ) => void;
}

export const ConfidenceChart: React.FC<ConfidenceChartProps> = ({
  data,
  width,
  height,
  themeColors = {
    backgroundColor: "#222222",
    textColor: "#ABABAB",
    lineColor: "#AD56D6",
    areaColors: {
      confidence95: "#BDA1C9",
      confidence75: "#7B3998",
    },
  },
  indicator,
  setIndicator,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (data.length > 0 && !indicator) {
      setIndicator({
        x: 0,
        y: data[0].percentile_50,
        name: data[0].year.toString(),
        value: data[0].percentile_50,
      });
    }
  }, [data, indicator, setIndicator]);

  const dataListOrder = [
    {
      name: "95% Confidence (Best case)",
      key: "percentile_95",
      color: themeColors.areaColors.confidence95,
    },
    {
      name: "75% Confidence",
      key: "percentile_75",
      color: themeColors.areaColors.confidence75,
    },
    {
      name: "Median",
      key: "percentile_50",
      color: themeColors.lineColor,
    },
    {
      name: "25% Confidence",
      key: "percentile_25",
      color: themeColors.areaColors.confidence75,
    },
    {
      name: "5% Confidence (Worst case)",
      key: "percentile_5",
      color: themeColors.areaColors.confidence95,
    },
  ];

  const option = useMemo(
    () => ({
      backgroundColor: themeColors.backgroundColor,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          label: {
            backgroundColor: themeColors.backgroundColor,
          },
        },
        formatter: function (params: any) {
          const dataIndex = params[0].dataIndex;
          const currentData = data[dataIndex];

          const orderedParams = [...params].sort((a, b) => {
            const aIndex = dataListOrder.findIndex(
              (item) => item.name === a.seriesName,
            );
            const bIndex = dataListOrder.findIndex(
              (item) => item.name === b.seriesName,
            );
            return aIndex - bIndex;
          });

          return orderedParams
            .map((item: any) => {
              const dataItem = dataListOrder.find(
                (d) => d.name === item.seriesName,
              );
              if (!dataItem) return "";
              return `${item.marker} ${item.seriesName}: ${formatNumberWithIntl({ num: currentData[dataItem.key] })}`;
            })
            .join("<br/>");
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.map((item) => item.year),
        axisTick: { show: false },
        axisLine: {
          lineStyle: {
            color: themeColors.textColor,
          },
        },
        axisLabel: {
          color: themeColors.textColor,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: themeColors.textColor,
          },
        },
        axisLabel: {
          color: themeColors.textColor,
          formatter: (value: number) => formatNumberWithIntl({ num: value }),
        },
        splitLine: {
          lineStyle: {
            color: "#FFFFFF33",
            type: "dashed",
          },
        },
      },
      series: [
        // 95% confidence interval base
        {
          name: "5% Confidence (Worst case)",
          type: "line",
          smooth: true,
          stack: "confidence95",
          symbol: "none",
          lineStyle: {
            color: themeColors.areaColors.confidence95,
            width: 1,
          },
          itemStyle: {
            color: themeColors.areaColors.confidence95,
          },
          data: data.map((item) => item.percentile_5),
        },
        // 95% confidence interval area
        {
          name: "95% Confidence (Best case)",
          type: "line",
          smooth: true,
          stack: "confidence95",
          symbol: "none",
          lineStyle: {
            color: themeColors.areaColors.confidence95,
            width: 1,
          },
          itemStyle: {
            color: themeColors.areaColors.confidence95,
          },
          areaStyle: {
            color: themeColors.areaColors.confidence95,
            opacity: 0.8,
          },
          data: data.map((item) => item.percentile_95 - item.percentile_5),
        },
        // 75% confidence interval base
        {
          name: "25% Confidence",
          type: "line",
          smooth: true,
          stack: "confidence75",
          symbol: "none",
          lineStyle: {
            color: themeColors.areaColors.confidence75,
            width: 1,
          },
          itemStyle: {
            color: themeColors.areaColors.confidence75,
          },
          data: data.map((item) => item.percentile_25),
        },
        // 75% confidence interval area
        {
          name: "75% Confidence",
          type: "line",
          smooth: true,
          stack: "confidence75",
          symbol: "none",
          lineStyle: {
            color: themeColors.areaColors.confidence75,
            width: 1,
          },
          itemStyle: {
            color: themeColors.areaColors.confidence75,
          },
          areaStyle: {
            color: themeColors.areaColors.confidence75,
            opacity: 0.8,
          },
          data: data.map((item) => item.percentile_75 - item.percentile_25),
        },
        // 50% confidence interval area
        {
          name: "Median",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: {
            color: themeColors.lineColor,
            width: 2,
          },
          itemStyle: {
            color: themeColors.lineColor,
          },
          data: data.map((item) => item.percentile_50),
          z: 10,
          zlevel: 1,
          markLine: indicator
            ? {
                data: [
                  {
                    xAxis: indicator.name,
                  },
                ],
                animation: false,
                symbol: "none",
                lineStyle: {
                  color: themeColors.textColor,
                  type: [5, 10],
                },
                label: {
                  show: false,
                },
              }
            : undefined,
        },
      ],
    }),
    [data, themeColors, indicator],
  );

  const handleChartClick = useCallback(
    (event: any) => {
      if (!chartInstance.current) return;

      const pointInPixel = [event.offsetX, event.offsetY];
      const pointInGrid = chartInstance.current.convertFromPixel(
        { seriesIndex: 0 },
        pointInPixel,
      );

      if (pointInGrid) {
        const [xValue, yValue] = pointInGrid;
        const index = Math.round(xValue);

        if (index >= 0 && index < data.length) {
          setIndicator({
            x: xValue,
            y: yValue,
            name: data[index].year.toString(),
            value: data[index].percentile_50,
          });

          chartInstance.current.setOption({
            series: [
              {},
              {},
              {},
              {},
              {},
              {
                markLine: {
                  data: [
                    {
                      xAxis: data[index].year.toString(),
                    },
                  ],
                  animation: false,
                  symbol: "none",
                  lineStyle: {
                    color: themeColors.textColor,
                    type: [5, 10],
                  },
                  label: {
                    show: false,
                  },
                },
              },
            ],
          });
        }
      }
    },
    [data, setIndicator],
  );

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    try {
      chartInstance.current.setOption(option);

      const zr = chartInstance.current.getZr();
      zr.off("click");
      zr.on("click", handleChartClick);
    } catch (error) {
      console.error("Error setting chart options:", error);
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data, themeColors, setIndicator, indicator]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const indicatorContent = useMemo(() => {
    if (!indicator || !data[Math.round(indicator.x)]) return null;

    return (
      <div className="indicator-info">
        <div className="selected" style={styles.infoRow}>
          <div className="name" style={styles.item}>
            Year
          </div>
          <span className="value">{indicator.name}</span>
        </div>
        {dataListOrder.map((item) => (
          <div key={item.key} className="selected" style={styles.infoRow}>
            <div className="name" style={styles.item}>
              <div
                style={{
                  ...styles.colorDot,
                  backgroundColor: item.color,
                }}
              />
              <span>{item.name}</span>
            </div>
            <span className="value">
              {formatNumberWithIntl({
                num: data[Math.round(indicator.x)][item.key],
              })}
            </span>
          </div>
        ))}
      </div>
    );
  }, [indicator, data]);

  return (
    <div className="chart">
      <div className="title">
        <span className="title-name">Projected liquid net worth</span>
      </div>
      <div
        ref={chartRef}
        style={{ width: width ? width : "100%", height: height ? height : 400 }}
      />
      {indicatorContent}
    </div>
  );
};

const styles = {
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorDot: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
  },
} as const;
